import { get } from "@github/webauthn-json";
import { useState } from "react";

import { useTranslatedToasts } from "hooks/toasts/useTranslatedToasts";

import {
  useFinalizeTransactionSigningMutation,
  useInitializeTransactionSigningMutation,
} from "../../api/TransactionSigningApi";
import { AUTH_TOASTS } from "../../constants/AuthToasts";

const NOT_ALLOWED_ERROR = "NotAllowedError";
const HTTP_STATUS_TOO_MANY_REQUESTS = 429;

export function useTransactionSigning() {
  const [initializeTransactionSigning] =
    useInitializeTransactionSigningMutation();
  const [finalizeTransactionSigning] = useFinalizeTransactionSigningMutation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    showTransactionInitiationSigningFailureToast,
    showTransactionFinalizationSigningFailureToast,
    showTransactionSigningRateLimitedToast,
  } = useTranslatedToasts(AUTH_TOASTS, "auth");

  async function signTransaction(
    transactionId: string | string[]
  ): Promise<string | undefined> {
    setIsLoading(true);

    try {
      const transactionIds = Array.isArray(transactionId)
        ? transactionId
        : [transactionId];

      const initResponse = await initializeTransactionSigning({
        transactionIds,
      });

      if (initResponse.data === undefined) {
        if (isRateLimitError(initResponse)) {
          showTransactionSigningRateLimitedToast();
        } else {
          showTransactionInitiationSigningFailureToast();
        }

        return;
      }

      const credentials = await get(initResponse.data);

      if (!credentials) {
        return;
      }

      const finalizeResponse = await finalizeTransactionSigning(credentials);

      if (finalizeResponse.data === undefined) {
        if (isRateLimitError(finalizeResponse)) {
          showTransactionSigningRateLimitedToast();
        } else {
          showTransactionFinalizationSigningFailureToast();
        }

        return;
      }

      return finalizeResponse.data.token;
    } catch (error) {
      if (error instanceof DOMException && error.name === NOT_ALLOWED_ERROR) {
        return;
      }

      showTransactionFinalizationSigningFailureToast();
    } finally {
      setIsLoading(false);
    }
  }

  return {
    signTransaction,
    isLoading,
  };
}

function isRateLimitError(response: Record<string, unknown>): boolean {
  if (!("error" in response) || !response.error) {
    return false;
  }

  const error = response.error;

  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === HTTP_STATUS_TOO_MANY_REQUESTS
  );
}
